import { z } from 'zod';
import { envelope, IdString, IsoDate, MoneyString, MonthPeriod, Period, Platform } from './common.js';

/** Tham số chung: pháp nhân, kỳ, shop (id của shop_entity_map, bỏ trống = tất cả). */
export const ScopeQuery = z.object({
  entity: IdString,
  period: Period,
  shop: IdString.optional(),
});
export type ScopeQuery = z.infer<typeof ScopeQuery>;

// ---------------------------------------------------------------- Tổng quan
export const Overview = z.object({
  period: Period,
  from: IsoDate,
  to: IsoDate,
  orders: z.object({ delivered: z.number(), returned: z.number(), cancelled: z.number() }),
  revenue: z.object({ gross: MoneyString, preTax: MoneyString, vatOut: MoneyString }),
  invoices: z.object({ original: z.number(), adjustment: z.number() }),
  fees: MoneyString,
  payouts: MoneyString,
  exceptions: z.object({ open: z.number(), blocking: z.number(), byKind: z.record(z.string(), z.number()) }),
  daily: z.array(z.object({ date: IsoDate, orders: z.number(), gross: MoneyString })),
});
export type Overview = z.infer<typeof Overview>;
export const OverviewResponse = envelope(Overview);

// ---------------------------------------------------------------- Hóa đơn
export const InvoiceKind = z.enum(['ORIGINAL', 'ADJUSTMENT', 'REPLACEMENT']);
export const InvoiceStatus = z.enum(['ISSUED', 'CANCELLED']);

export const InvoiceListQuery = ScopeQuery.extend({
  kind: InvoiceKind.optional(),
  /** Số hóa đơn hoặc mã đơn sàn. */
  q: z.string().trim().max(64).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type InvoiceListQuery = z.infer<typeof InvoiceListQuery>;

export const InvoiceRow = z.object({
  id: IdString,
  serial: z.string(),
  number: z.number(),
  issuedDate: IsoDate,
  kind: InvoiceKind,
  status: InvoiceStatus,
  buyerName: z.string(),
  platform: Platform.nullable(),
  orderSn: z.string().nullable(),
  preTax: MoneyString,
  vatRate: z.string(),
  vatAmount: MoneyString,
  total: MoneyString,
  cqtCode: z.string().nullable(),
});
export type InvoiceRow = z.infer<typeof InvoiceRow>;

export const InvoiceListResponse = envelope(
  z.object({
    items: z.array(InvoiceRow),
    totals: z.object({ count: z.number(), preTax: MoneyString, vatAmount: MoneyString, total: MoneyString }),
  }),
);
export type InvoiceListResponse = z.infer<typeof InvoiceListResponse>;

export const ExceptionKind = z.enum([
  'MISSING_INVOICE',
  'DUPLICATE_INVOICE',
  'AMOUNT_MISMATCH',
  'LATE_INVOICE',
  'RETURN_NOT_ADJUSTED',
  'INVOICE_WITHOUT_ORDER',
]);
export type ExceptionKind = z.infer<typeof ExceptionKind>;
export const ExceptionStatus = z.enum(['OPEN', 'CLOSED', 'RESOLVED']);
export type ExceptionStatus = z.infer<typeof ExceptionStatus>;

export const InvoiceDetail = InvoiceRow.extend({
  issuedAt: z.string(),
  shopId: z.string().nullable(),
  adjusts: InvoiceRow.pick({ id: true, serial: true, number: true, issuedDate: true, total: true }).nullable(),
  adjustments: z.array(InvoiceRow.pick({ id: true, serial: true, number: true, issuedDate: true, total: true })),
  order: z
    .object({
      orderSn: z.string(),
      status: z.string(),
      deliveredDate: IsoDate.nullable(),
      grossAmount: MoneyString,
      itemSummary: z.string(),
    })
    .nullable(),
  settlement: z.array(z.object({ feeCode: z.string(), amount: MoneyString, settledDate: IsoDate })),
  exceptions: z.array(z.object({ id: IdString, kind: z.string(), status: ExceptionStatus })),
});
export type InvoiceDetail = z.infer<typeof InvoiceDetail>;
export const InvoiceDetailResponse = envelope(InvoiceDetail);

// ---------------------------------------------------------------- Đối soát R1
export const ExceptionListQuery = z.object({
  entity: IdString,
  period: MonthPeriod,
  status: ExceptionStatus.optional(),
  kind: ExceptionKind.optional(),
});
export type ExceptionListQuery = z.infer<typeof ExceptionListQuery>;

export const ExceptionRow = z.object({
  id: IdString,
  subjectKey: z.string(),
  kind: ExceptionKind,
  severity: z.enum(['BLOCKING', 'WARNING', 'INFO']),
  status: ExceptionStatus,
  diffAmount: MoneyString,
  detail: z.record(z.string(), z.unknown()),
  ruleVersion: z.number(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  resolution: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  resolvedAt: z.string().nullable(),
});
export type ExceptionRow = z.infer<typeof ExceptionRow>;

export const ExceptionListResponse = envelope(
  z.object({
    items: z.array(ExceptionRow),
    summary: z.object({
      byStatus: z.record(z.string(), z.number()),
      byKind: z.record(z.string(), z.number()),
    }),
  }),
);
export type ExceptionListResponse = z.infer<typeof ExceptionListResponse>;

export const CloseExceptionRequest = z.object({
  entity: IdString,
  conclusion: z.string().trim().min(10, 'Kết luận cần ít nhất 10 ký tự').max(2000),
});
export type CloseExceptionRequest = z.infer<typeof CloseExceptionRequest>;
export const CloseExceptionResponse = envelope(ExceptionRow);

export const RunReconRequest = z.object({ entity: IdString, period: MonthPeriod });
export type RunReconRequest = z.infer<typeof RunReconRequest>;
export const RunReconResponse = envelope(
  z.object({
    period: MonthPeriod,
    ruleVersion: z.number(),
    orders: z.number(),
    invoices: z.number(),
    exceptions: z.number(),
    byKind: z.record(z.string(), z.number()),
    opened: z.number(),
    reopened: z.number(),
    resolved: z.number(),
  }),
);

// ---------------------------------------------------------------- Dòng tiền
export const Cashflow = z.object({
  period: Period,
  from: IsoDate,
  to: IsoDate,
  /** Giá trị hàng trên settlement trong kỳ (ITEM_PRICE). */
  gmv: MoneyString,
  refunds: MoneyString,
  fees: z.array(z.object({ feeCode: z.string(), amount: MoneyString })),
  feesTotal: MoneyString,
  netSettlement: MoneyString,
  payoutsInPeriod: MoneyString,
  /** Sàn còn giữ cuối kỳ: tổng ròng đã quyết toán trừ tổng đã chuyển về, lũy kế đến hết kỳ. */
  heldAtEnd: MoneyString,
  byShop: z.array(
    z.object({ platform: Platform, shopId: z.string(), gmv: MoneyString, fees: MoneyString, net: MoneyString, paid: MoneyString }),
  ),
  payouts: z.array(
    z.object({
      payoutDate: IsoDate,
      platform: Platform,
      shopId: z.string(),
      payoutRef: z.string(),
      amount: MoneyString,
      bankAccountMasked: z.string().nullable(),
    }),
  ),
});
export type Cashflow = z.infer<typeof Cashflow>;
export const CashflowResponse = envelope(Cashflow);

export const EXCEPTION_KIND_LABEL: Record<ExceptionKind, string> = {
  MISSING_INVOICE: 'Thiếu hóa đơn',
  DUPLICATE_INVOICE: 'Trùng hóa đơn',
  AMOUNT_MISMATCH: 'Lệch tiền',
  LATE_INVOICE: 'Lập trễ',
  RETURN_NOT_ADJUSTED: 'Trả hàng chưa điều chỉnh',
  INVOICE_WITHOUT_ORDER: 'Hóa đơn không có đơn',
};

export const FEE_LABEL: Record<string, string> = {
  ITEM_PRICE: 'Tiền hàng',
  COMMISSION_FEE: 'Phí cố định / hoa hồng',
  TRANSACTION_FEE: 'Phí thanh toán',
  SERVICE_FEE: 'Phí dịch vụ',
  AFFILIATE_COMMISSION: 'Hoa hồng tiếp thị liên kết',
  REFUND: 'Hoàn tiền trả hàng',
  RETURN_SHIPPING_FEE: 'Phí vận chuyển trả hàng',
};

export type OverviewResponse = z.infer<typeof OverviewResponse>;
export type InvoiceDetailResponse = z.infer<typeof InvoiceDetailResponse>;
export type CloseExceptionResponse = z.infer<typeof CloseExceptionResponse>;
export type RunReconResponse = z.infer<typeof RunReconResponse>;
export type CashflowResponse = z.infer<typeof CashflowResponse>;
